import { useCallback, useEffect, useRef } from 'react';

type UseScrollTabsObserverOptions<T extends { id: string | number }> = {
  items: T[];
  containerRef: React.RefObject<HTMLElement | null>;
  onChange?: (id: T['id']) => void;
  dataAttribute?: string;
  getElementId?: (id: T['id']) => string;
  axis?: 'x' | 'y';
};

type UseScrollTabsObserverReturn<T extends { id: string | number }> = {
  register: (id: T['id'], el: HTMLElement | null) => void;
  sectionRefs: React.RefObject<Map<string | number, HTMLElement>>;
  isProgrammaticScroll: React.RefObject<boolean>;
};

export function useScrollTabsObserver<T extends { id: string | number }>({
  items,
  containerRef,
  onChange,
  dataAttribute = 'data-item-id',
  getElementId,
  axis = 'y',
}: UseScrollTabsObserverOptions<T>): UseScrollTabsObserverReturn<T> {
  const sectionRefs = useRef<Map<string | number, HTMLElement>>(new Map());
  const isProgrammaticScroll = useRef(false);
  const itemsRef = useRef(items);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const register = useCallback(
    (id: T['id'], el: HTMLElement | null): void => {
      if (el) {
        sectionRefs.current.set(id, el);
        el.setAttribute(dataAttribute, String(id));
        if (getElementId) el.id = getElementId(id);
      } else {
        sectionRefs.current.delete(id);
      }
    },
    [dataAttribute, getElementId]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const handleScroll = (): void => {
      if (isProgrammaticScroll.current) return;

      const scrollPos = axis === 'x' ? container.scrollLeft : container.scrollTop;
      const clientSize = axis === 'x' ? container.clientWidth : container.clientHeight;
      const scrollSize = axis === 'x' ? container.scrollWidth : container.scrollHeight;

      if (scrollSize - scrollPos - clientSize < 20) {
        const last = itemsRef.current[itemsRef.current.length - 1];
        if (last) onChangeRef.current?.(last.id);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const focalPoint = containerRect[axis === 'x' ? 'left' : 'top'] + clientSize * 0.2;

      let activeId: string | number | null = null;
      for (const item of itemsRef.current) {
        const el = sectionRefs.current.get(item.id);
        if (!el) continue;
        const elRect = el.getBoundingClientRect();
        const elStart = axis === 'x' ? elRect.left : elRect.top;
        if (elStart <= focalPoint) {
          activeId = item.id;
        }
      }

      if (activeId !== null) onChangeRef.current?.(activeId);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return (): void => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [axis, containerRef]);

  return { register, sectionRefs, isProgrammaticScroll };
}
